import type { ShapeInterface } from '~/types/grid.types'

export const getShapeListBounds = (
  shapeList: ShapeInterface[],
  startingBounds: { minX: number; minY: number; maxX: number; maxY: number },
  _gridPattern?: string
) => {
  let gridPattern = null
  try {
    const gridStore = useGridStore()
    gridPattern = gridStore.gridPattern
  } catch (e) {
    gridPattern = _gridPattern
  }

  const currentGridPattern = gridPattern
  const collectAllPoints = currentGridPattern?.id === '45-deg'

  const bounds = shapeList.reduce(
    (acc: any, copy: any) => {
      const shape = { ...copy, attrs: { ...copy.attrs } }
      shape.attrs = { ...shape, ...shape.attrs }

      const x = shape.attrs.x
      const y = shape.attrs.y
      const width = shape.attrs.width
      const height = shape.attrs.height
      let rotatedBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null
      const rotation = shape.attrs.rotation || 0

      if (shape.attrs.type === 'arc') {
        const configuredRadius = shape.attrs.outerRadius ?? shape.attrs.radius
        const arcRadius = Number.isFinite(configuredRadius)
          ? configuredRadius
          : shape.attrs.angle === 90
            ? Math.max(width, height)
            : Math.max(width, height) / 2

        const centerX = x
        const centerY = y
        const numSamples = 32
        const angleSpan = (shape.attrs.angle * Math.PI) / 180
        const startAngle = (rotation * Math.PI) / 180
        const endAngle = startAngle + angleSpan

        const points: { x: number; y: number }[] = []
        points.push({ x: centerX, y: centerY })

        for (let i = 0; i <= numSamples; i++) {
          const angle = startAngle + (endAngle - startAngle) * (i / numSamples)
          const px = centerX + arcRadius * Math.cos(angle)
          const py = centerY + arcRadius * Math.sin(angle)
          points.push({ x: px, y: py })
        }

        points.push({
          x: centerX + arcRadius * Math.cos(startAngle),
          y: centerY + arcRadius * Math.sin(startAngle),
        })
        points.push({
          x: centerX + arcRadius * Math.cos(endAngle),
          y: centerY + arcRadius * Math.sin(endAngle),
        })

        const xs = points.map((pt) => pt.x)
        const ys = points.map((pt) => pt.y)
        rotatedBounds = {
          minX: Math.min(...xs),
          maxX: Math.max(...xs),
          minY: Math.min(...ys),
          maxY: Math.max(...ys),
        }
      } else if (shape.attrs.type === 'rect-triangle') {
        const side = width
        const points = [
          { x: -side / 2, y: -side / 2 },
          { x: side / 2, y: -side / 2 },
          { x: side / 2, y: side / 2 },
        ]

        const rotationRad = (rotation * Math.PI) / 180
        const rotatedPoints = points.map((pt) => {
          const xRot = pt.x * Math.cos(rotationRad) - pt.y * Math.sin(rotationRad)
          const yRot = pt.x * Math.sin(rotationRad) + pt.y * Math.cos(rotationRad)
          return { x: x + xRot, y: y + yRot }
        })

        const xs = rotatedPoints.map((pt) => pt.x)
        const ys = rotatedPoints.map((pt) => pt.y)
        rotatedBounds = {
          minX: Math.min(...xs),
          maxX: Math.max(...xs),
          minY: Math.min(...ys),
          maxY: Math.max(...ys),
        }
      } else {
        let points: { x: number; y: number }[] = []

        switch (shape.attrs.type) {
          case 'rect': {
            const halfWidth = width / 2
            const halfHeight = height / 2
            points = [
              { x: -halfWidth, y: -halfHeight },
              { x: halfWidth, y: -halfHeight },
              { x: halfWidth, y: halfHeight },
              { x: -halfWidth, y: halfHeight },
            ]
            break
          }
          case 'diamond': {
            const halfWidth = width / 2
            const halfHeight = height / 2
            points = [
              { x: -halfWidth, y: 0 },
              { x: 0, y: -halfHeight },
              { x: halfWidth, y: 0 },
              { x: 0, y: halfHeight },
            ]
            break
          }
          case 'triangle': {
            const halfWidth = width / 2
            const halfHeight = height / 2
            points = [
              { x: -halfWidth, y: halfHeight },
              { x: 0, y: -halfHeight },
              { x: halfWidth, y: halfHeight },
            ]
            break
          }
          case 'hex': {
            const radius = (copy as any).radius || shape.attrs.radius || Math.min(width, height) / 2
            const sides = (copy as any).sides || shape.attrs.sides || 6
            points = []
            for (let i = 0; i < sides; i++) {
              const angle = (2 * Math.PI * i) / sides - Math.PI / 2
              points.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) })
            }
            break
          }
          default: {
            const halfWidth = width / 2
            const halfHeight = height / 2
            points = [
              { x: -halfWidth, y: -halfHeight },
              { x: halfWidth, y: -halfHeight },
              { x: halfWidth, y: halfHeight },
              { x: -halfWidth, y: halfHeight },
            ]
            break
          }
        }

        const rotationRad = (rotation * Math.PI) / 180
        const rotatedPoints = points.map((pt) => {
          const xRot = pt.x * Math.cos(rotationRad) - pt.y * Math.sin(rotationRad)
          const yRot = pt.x * Math.sin(rotationRad) + pt.y * Math.cos(rotationRad)
          return { x: x + xRot, y: y + yRot }
        })

        const xs = rotatedPoints.map((pt) => pt.x)
        const ys = rotatedPoints.map((pt) => pt.y)
        rotatedBounds = {
          minX: Math.min(...xs),
          maxX: Math.max(...xs),
          minY: Math.min(...ys),
          maxY: Math.max(...ys),
        }
      }

      if (!rotatedBounds) {
        rotatedBounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 }
      }

      if (collectAllPoints) {
        if (!acc._allPoints) acc._allPoints = [] as { x: number; y: number }[]
        acc._allPoints.push(
          { x: rotatedBounds.minX, y: rotatedBounds.minY },
          { x: rotatedBounds.maxX, y: rotatedBounds.minY },
          { x: rotatedBounds.maxX, y: rotatedBounds.maxY },
          { x: rotatedBounds.minX, y: rotatedBounds.maxY }
        )
      }
      return {
        minX: Math.min(acc.minX, rotatedBounds.minX),
        minY: Math.min(acc.minY, rotatedBounds.minY),
        maxX: Math.max(acc.maxX, rotatedBounds.maxX),
        maxY: Math.max(acc.maxY, rotatedBounds.maxY),
        avgX:
          (Math.max(acc.maxX, rotatedBounds.maxX) - Math.min(acc.minX, rotatedBounds.minX)) / 2,
        avgY:
          (Math.max(acc.maxY, rotatedBounds.maxY) - Math.min(acc.minY, rotatedBounds.minY)) / 2,
        ...(collectAllPoints ? { _allPoints: acc._allPoints } : {}),
      }
    },
    {
      ...startingBounds,
      avgX: 0,
      avgY: 0,
      ...(collectAllPoints ? { _allPoints: [] as { x: number; y: number }[] } : {}),
    } as any
  )

  if (collectAllPoints && bounds._allPoints && bounds._allPoints.length > 0) {
    const allX = bounds._allPoints.map((pt: any) => pt.x)
    const allY = bounds._allPoints.map((pt: any) => pt.y)
    bounds.minX = Math.min(...allX)
    bounds.maxX = Math.max(...allX)
    bounds.minY = Math.min(...allY)
    bounds.maxY = Math.max(...allY)
  }

  return bounds
}
